'use client';

import { useEffect } from 'react';

interface ScrollLockSnapshot {
  htmlOverflow: string;
  bodyOverflow: string;
  bodyPaddingRight: string;
}

let activeScrollLocks = 0;
let scrollLockSnapshot: ScrollLockSnapshot | null = null;

function getScrollbarWidth(): number {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 0;
  }

  return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
}

export function acquireBodyScrollLock(): () => void {
  if (typeof document === 'undefined') {
    return () => {};
  }

  if (activeScrollLocks === 0) {
    scrollLockSnapshot = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
      bodyPaddingRight: document.body.style.paddingRight,
    };

    const scrollbarWidth = getScrollbarWidth();
    const bodyStyle = document.defaultView?.getComputedStyle(document.body);
    const bodyOverflow = bodyStyle?.overflowY || bodyStyle?.overflow || document.body.style.overflow;
    const bodyIsScrollContainer = document.scrollingElement === document.body
      || /^(auto|scroll|hidden|overlay)$/.test(bodyOverflow);
    document.documentElement.style.overflow = 'hidden';
    // 首页 body 高度为 100%。将 visible 改成 hidden 会新建滚动容器，
    // 截断文档高度并把根滚动位置夹到 0；只锁已有的滚动容器。
    if (bodyIsScrollContainer) document.body.style.overflow = 'hidden';

    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  activeScrollLocks += 1;

  let released = false;

  return () => {
    if (released || typeof document === 'undefined') {
      return;
    }

    released = true;
    activeScrollLocks = Math.max(0, activeScrollLocks - 1);

    if (activeScrollLocks > 0 || !scrollLockSnapshot) {
      return;
    }

    document.documentElement.style.overflow = scrollLockSnapshot.htmlOverflow;
    document.body.style.overflow = scrollLockSnapshot.bodyOverflow;
    document.body.style.paddingRight = scrollLockSnapshot.bodyPaddingRight;
    scrollLockSnapshot = null;
  };
}

export function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) {
      return;
    }

    return acquireBodyScrollLock();
  }, [locked]);
}
